/**
 * Is this workspace path part of the SYSTEM tier the lawyer must never see?
 *
 * ## Why this exists (DS-37)
 *
 * `case-workspace-guide.md` §2.3 states that `.lexiun/` "is hidden from the
 * lawyer in LexIDE". That promise had only ever been enforced on the WRITE side
 * — no disclosure-side check existed anywhere in the chat surface — so the
 * activity trail rendered `.lexiun/CASE.md` and `.lexiun/FOLDERS.md` verbatim on
 * every case's first substantive turn, because `case-craft.md` §1 makes reading
 * both a mandatory orientation step. Measured red on 2026-08-13: 20 leaking rows
 * on one case.
 *
 * ## ⚠ This is a MIRROR, not a new rule
 *
 * The authority is server-side: `RESERVED_SEGMENTS` + `isSystemPath()` in
 * `lexiun-core/src/tools/resolve-handle.ts:329,344`. This file reproduces that
 * logic for the client and must keep matching it — including the parts that look
 * like oversights and are not:
 *
 *   - `.lexiunBackup/notes.md` is an ORDINARY user folder and must render
 *     normally. A hand-rolled `startsWith(".lexiun")` would swallow it.
 *   - `.gitignore` / `.gitattributes` are ordinary FILES, not paths under
 *     `.git/`, and likewise stay visible.
 *   - both separators are checked, exactly as the server does.
 *
 * If the server's list changes, change this with it. A client mirror that drifts
 * from its authority is worse than no mirror, because it looks authoritative.
 *
 * ## Where it is applied
 *
 * At the two INGEST boundaries, never in the renderers — so a path never enters
 * a block and none of the six path-printing renderers can forget the check,
 * including ones not written yet:
 *
 *   live    `session.tool` frames     → `tool-activity.ts`
 *   reload  persisted `v:2` toolTrail → `useChatSession.ts`
 *
 * Two, not one, because live and persisted are genuinely different pipes; two,
 * not six, because gating at ingest is what makes the renderers safe by
 * construction.
 */

/** Mirrors the server's `RESERVED_SEGMENTS` (resolve-handle.ts:329). */
const RESERVED_SEGMENTS = [".lexiun", ".git"] as const;

/**
 * `true` when a case-relative path is the System tier or lives inside it.
 *
 * Takes the path already relative to the case root — which is what both the
 * tool-call wire and the persisted trail carry, so no resolution is needed
 * client-side (the server's version does the `relative()` first).
 */
export function isSystemPath(path: string | undefined | null): boolean {
  if (!path) return false;
  // Normalise a leading "./" and any accidental leading slash so a path that
  // means the same thing cannot slip past by spelling.
  const rel = path.trim().replace(/^\.?[/\\]+/, "");
  return RESERVED_SEGMENTS.some(
    (seg) => rel === seg || rel.startsWith(`${seg}/`) || rel.startsWith(`${seg}\\`),
  );
}
